/* Ruby Rose — wishlist.

   Saved items live in this browser's localStorage. A wishlist that follows the
   customer between devices needs an account or an app behind it; this keeps
   the hearts, the header count and the wishlist panel telling the same story
   on one device, which is what the storefront promises today.

   Each heart carries the handful of fields the panel needs to draw a row, so
   opening the wishlist costs no requests and works offline. The trade is that
   a title or price edited in the admin will not reach an item already saved —
   the link always goes to the live product page, so nothing is ever wrong for
   longer than a click. */

(function () {
  const KEY = "ruby:wishlist";
  const CHANGED = "ruby:wishlist:change";

  /* ---------------- Store ---------------- */
  function read() {
    try {
      const raw = JSON.parse(localStorage.getItem(KEY));
      return Array.isArray(raw) ? raw.filter((item) => item && item.id) : [];
    } catch {
      // Private browsing, a full quota, or storage edited by hand.
      return [];
    }
  }

  function write(items) {
    try {
      localStorage.setItem(KEY, JSON.stringify(items));
    } catch {
      // Saving is best-effort; the UI below still reflects this session.
    }
    document.dispatchEvent(new CustomEvent(CHANGED, { detail: { items } }));
  }

  const sameId = (a, b) => String(a) === String(b);

  function toggle(entry) {
    const items = read();
    const at = items.findIndex((item) => sameId(item.id, entry.id));
    // Newest first, so the panel reads like a recently-saved list.
    at > -1 ? items.splice(at, 1) : items.unshift(entry);
    write(items);
  }

  function remove(id) {
    write(read().filter((item) => !sameId(item.id, id)));
  }

  function entryFrom(button) {
    return {
      id: button.dataset.rrWishlist,
      handle: button.dataset.rrHandle || "",
      title: button.dataset.rrTitle || "",
      url: button.dataset.rrUrl || "",
      image: button.dataset.rrImage || "",
      price: button.dataset.rrPrice || "",
    };
  }

  /* ---------------- Panel ---------------- */
  // Built as nodes rather than an HTML string: titles and prices are store
  // data, and assembling markup from them by hand invites an injection bug.
  function row(item) {
    const line = document.createElement("div");
    line.className = "rr-panel__line";

    if (item.image) {
      const img = document.createElement("img");
      img.src = item.image;
      img.alt = "";
      img.loading = "lazy";
      line.appendChild(img);
    } else {
      line.appendChild(document.createElement("span"));
    }

    const middle = document.createElement("div");

    const link = document.createElement("a");
    link.className = "rr-panel__line-title";
    link.href = item.url || "#";
    link.textContent = item.title;
    middle.appendChild(link);

    if (item.price) {
      const price = document.createElement("p");
      price.className = "rr-panel__line-meta";
      price.textContent = item.price;
      middle.appendChild(price);
    }

    line.appendChild(middle);

    const drop = document.createElement("button");
    drop.type = "button";
    drop.className = "rr-panel__line-remove";
    drop.dataset.rrWishlistRemove = item.id;
    drop.setAttribute("aria-label", `Remove ${item.title} from your wishlist`);
    drop.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 5l14 14M19 5L5 19" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';
    line.appendChild(drop);

    return line;
  }

  function renderPanel(items) {
    const body = document.querySelector("[data-rr-wishlist-body]");
    if (!body) return;

    // The empty copy is authored in the section, so keep a copy of the wording
    // before the first render replaces it.
    const existing = body.querySelector("[data-rr-wishlist-empty]");
    if (existing) body.dataset.rrEmptyText = existing.textContent.trim();
    const emptyText = body.dataset.rrEmptyText || "Your wishlist is empty.";

    body.textContent = "";

    if (!items.length) {
      const note = document.createElement("p");
      note.className = "rr-panel__lead";
      note.dataset.rrWishlistEmpty = "";
      note.textContent = emptyText;
      body.appendChild(note);
      return;
    }

    items.forEach((item) => body.appendChild(row(item)));
  }

  /* ---------------- Paint ---------------- */
  function paint() {
    const items = read();
    const saved = new Set(items.map((item) => String(item.id)));

    document.querySelectorAll("[data-rr-wishlist]").forEach((button) => {
      const on = saved.has(String(button.dataset.rrWishlist));
      const title = button.dataset.rrTitle || "this product";

      button.setAttribute("aria-pressed", String(on));
      button.classList.toggle("is-saved", on);
      button.setAttribute(
        "aria-label",
        on ? `Remove ${title} from your wishlist` : `Save ${title} to your wishlist`
      );
    });

    const badge = document.querySelector('[data-rr-badge="wishlist"]');
    if (badge) badge.textContent = String(items.length);

    renderPanel(items);
  }

  /* ---------------- Wiring ---------------- */
  document.addEventListener("click", (event) => {
    const heart = event.target.closest("[data-rr-wishlist]");
    if (heart) {
      // Hearts sit inside the card's link on some layouts.
      event.preventDefault();
      event.stopPropagation();
      toggle(entryFrom(heart));
      return;
    }

    const drop = event.target.closest("[data-rr-wishlist-remove]");
    if (drop) {
      event.preventDefault();
      remove(drop.dataset.rrWishlistRemove);
    }
  });

  document.addEventListener(CHANGED, paint);
  document.addEventListener("shopify:section:load", paint);
  // Another tab saving something should not leave this one out of date.
  window.addEventListener("storage", (event) => {
    if (event.key === KEY) paint();
  });

  /* Cards arrive after first paint in several places — the header button is
     injected by ruby-header.js, Handpicked renders its rail from JS, and the
     carousels can be re-rendered by the Section Rendering API. Repaint when
     new hearts appear rather than making every one of those call in. */
  function watch() {
    let queued = false;
    new MutationObserver((records) => {
      if (queued) return;
      const relevant = records.some((record) =>
        [...record.addedNodes].some(
          (node) =>
            node.nodeType === 1 &&
            (node.matches?.("[data-rr-wishlist], [data-rr-badge], [data-rr-wishlist-body]") ||
              node.querySelector?.("[data-rr-wishlist], [data-rr-badge], [data-rr-wishlist-body]"))
        )
      );
      if (!relevant) return;

      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        paint();
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  function init() {
    paint();
    watch();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }

  // Exposed so a future wishlist app or a PDP button can drive the same store.
  window.RubyWishlist = { read, has: (id) => read().some((i) => sameId(i.id, id)), toggle, remove, paint };
})();
